<!-- 
  @component
  ### Dropdown, fully customizable with mobile-sheet layout and buttons
  provide a trigger-slot to determine what will open the dropdown, 
  and a default-slot for the dropdown content
-->
<script lang="ts">
  import { BREAKPOINTS } from '$lib/constants';
  import type { Transition, Strategy, Placement, Offset } from './';
  import type { App } from '$lib/types';

  import { createEventDispatcher, getContext, setContext } from 'svelte';
  import { fade, slide } from 'svelte/transition';
  import { flyScale } from '$lib/transitions';
  import { offset as floatOffset, flip, shift } from 'svelte-floating-ui/core';
  import { createFloatingActions } from 'svelte-floating-ui';
  import { clickOutsideAction } from '$lib/directives/clickOutsideAction';
  import { portal as usePortal } from '$components/Portal.svelte';
  import { beforeNavigate } from '$app/navigation';
  import { Drawer } from '$mds/Dialogs';
  import Skeleton from '$mds/Dialogs/_Skeleton.svelte';

  // bindings
  export let el: HTMLElement | undefined = undefined;
  export let sheetEl: Skeleton | undefined = undefined;
  export let triggerEl: HTMLElement | undefined = undefined;
  export let contentEl: HTMLElement | undefined = undefined;
  let scrollerEl: HTMLElement | undefined = undefined;

  // state
  export let isOpen = false;
  export let openDelay = 300;
  export let closeDelay = 300;

  // interaction
  export let title: string | undefined = undefined;
  export let disabled = false;
  export let hoverOpens = false;
  // Whether the dropdown should close when the mouse leaves the trigger. Only used if `hoverOpens` is true.
  export let hoverCloses = true;
  export let strategy: Strategy = 'absolute';
  export let placement: Placement = 'bottom-start';
  export let offset: Offset = [2, 0]; // [mainAxis, crossAxis] tailwind spacing
  export let transitionType: Transition = 'fade'; // default to fade for any random dropdown
  export let transitionDuration: undefined | number = undefined;
  export let portal: HTMLElement | string | null = null;
  export let maxHeight: undefined | number = 384;
  export let scrollerMinHeight = 'min-h-[30vh]';

  // classes
  export let size: 'sm' | 'base' | 'lg' = 'base';
  export let contentClass = 'p-1';
  export let wrapperClass = '';
  let className = '';
  export { className as class };
  export let scrollerClass = '';
  export let fullHeightSheet = false;

  const dropdownClass = 'absolute w-max top-0 left-0 rounded-2xl';
  const fadeClass = 'pointer-events-none absolute inset-x-0 bg-gradient-to-t to-transparent';
  $: minWidthClass = size === 'sm' ? 'min-w-52' : size === 'lg' ? 'min-w-68' : 'min-w-60';
  $: gapClass = `flex flex-col ${size === 'sm' ? 'gap-y-0.5' : size === 'lg' ? 'gap-y-2' : 'gap-y-1'}`;
  $: desktopHeight = maxHeight ? `max-height: ${maxHeight - 18}px` : ''; // 18px is the padding and border of the parent
  $: desktopPadding = (maxHeight && contentEl && maxHeight <= contentEl?.offsetHeight) as boolean; // add padding and fade if content is scrollable

  // functionality
  export const open = () => (isOpen = true);
  export const close = () => (isOpen = false);
  const dispatch = createEventDispatcher();
  const { innerWidth } = getContext<App>('app');
  $: isMobile = $innerWidth < BREAKPOINTS.sm;
  $: isSheetLayout = isMobile;

  let timers = {
    mouseClose: undefined as NodeJS.Timeout | undefined,
    mouseOpen: undefined as NodeJS.Timeout | undefined,
    clickOutside: undefined as NodeJS.Timeout | undefined
  };

  const [xTrigger, xContent] = createFloatingActions({
    strategy,
    placement,
    middleware: [floatOffset({ mainAxis: offset[0] * 4, crossAxis: offset[1] * 4 }), flip(), shift()]
  });

  $: floatingTrigger = node => {
    if (!isSheetLayout) {
      xTrigger(node);
    }
  };

  $: floatingContent = node => {
    if (!isSheetLayout && node) {
      xContent(node);
    }
  };

  export function toggle() {
    if (disabled) {
      return;
    }

    // dont toggle if the clickOutside timer is active
    // don't toggle on click if `hoverOpens` is true, and we are on desktop. Mobile still toggles.
    if (timers.clickOutside || (hoverOpens && !isMobile)) return;
    isOpen = !isOpen;
    dispatch('click', { detail: isOpen });
    clearTimers();
  }

  export function closeDropdownOrSheet() {
    // don't toggle on click if `hoverOpens` is true, and we are on desktop. Mobile still toggles.
    if (hoverOpens && !isMobile) return;
    clearTimers();

    timers.clickOutside = setTimeout(() => {
      if (isSheetLayout && sheetEl) {
        sheetEl.closeModal();
      } else {
        isOpen = false;
      }
      timers.clickOutside = undefined;
      focusTrigger();
    }, 16);
  }
  setContext('closeDropdownOrSheet', closeDropdownOrSheet);

  function handleMouseEnter() {
    if (isSheetLayout || !hoverOpens) return;
    clearTimers();
    timers.mouseOpen = setTimeout(() => {
      isOpen = true;
    }, openDelay);
  }

  function handleMouseLeave() {
    if (isSheetLayout || !hoverOpens || !hoverCloses) return;
    clearTimers();
    timers.mouseClose = setTimeout(() => {
      isOpen = false;
    }, closeDelay);
  }

  function clearTimers() {
    clearTimeout(timers.mouseClose);
    clearTimeout(timers.mouseOpen);
  }

  function handleDispatch(isOpen) {
    dispatch(isOpen ? 'open' : 'close');
  }

  function customTransition(element: Element) {
    if (!element) return;
    if (transitionType === 'fade') {
      return fade(element, { duration: transitionDuration ?? 200 });
    }
    if (transitionType === 'slide') {
      return slide(element, { duration: transitionDuration ?? 200, axis: 'y' });
    }
    if (transitionType === 'flyScale') {
      let translateY = placement.startsWith('top') ? 20 : -20;
      return flyScale(element, {
        duration: transitionDuration ?? 300,
        translate: { y: translateY, x: 0 },
        scale: { x: 0.75, y: 0.75 }
      });
    }
  }

  $: handleDispatch(isOpen);

  function handleEscape(event) {
    if (event.key === 'Escape') {
      if (
        event.target === triggerEl ||
        event.target === contentEl ||
        event.target === scrollerEl ||
        triggerEl?.contains(event.target) ||
        contentEl?.contains(event.target) ||
        scrollerEl?.contains(event.target)
      ) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        closeDropdownOrSheet();
      }
    }
  }

  function focusTrigger() {
    triggerEl?.focus();
  }

  beforeNavigate(() => {
    closeDropdownOrSheet();
  });
</script>

<!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
<div class="dropdown-wrapper {wrapperClass}" bind:this={el} on:keyup={handleEscape}>
  <div
    bind:this={triggerEl}
    use:floatingTrigger
    on:click={toggle}
    on:mouseenter={handleMouseEnter}
    on:mouseleave={handleMouseLeave}
    class="dropdown-trigger z-1"
  >
    {#if $$slots.trigger}
      <slot name="trigger" />
    {/if}
  </div>

  <!-- 
        
    Mobile / Sheet Layout

  -->

  {#if isSheetLayout && isOpen}
    <Drawer
      bind:isOpen
      bind:drawer={sheetEl}
      bind:scrollerEl
      scrollerClass="w-full overflow-y-auto p-2 {scrollerEl && scrollerEl.scrollHeight > scrollerEl.clientHeight
        ? 'pb-16'
        : 'pb-2'} {scrollerClass} {gapClass}"
      on:closed={focusTrigger}
      class={className}
      {title}
      {fullHeightSheet}
      {scrollerMinHeight}
    >
      <slot />
      <span
        class="{fadeClass} from-ondark-base bottom-1 h-16 {scrollerEl &&
        scrollerEl.scrollHeight > scrollerEl.clientHeight
          ? ''
          : 'hidden'}"
      ></span>
    </Drawer>

    <!--

      Desktop / Dropdown Layout

    -->
  {:else if isOpen}
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <div
      bind:this={contentEl}
      use:floatingContent
      use:clickOutsideAction
      use:usePortal={portal}
      transition:customTransition
      on:mouseenter={handleMouseEnter}
      on:mouseleave={handleMouseLeave}
      on:click={() => dispatch('clickInside')}
      on:clickoutside={closeDropdownOrSheet}
      role="dialog"
      class="dropdown-content z-dropdown border-ondark-subtle max-w-100 border shadow-lg {contentClass} {className} {dropdownClass} {minWidthClass} {contentClass.includes(
        'bg-'
      )
        ? ''
        : 'bg-ondark-base'}"
      class:dropdown-small={size === 'sm'}
      class:origin-bottom-right={placement === 'top-end'}
      class:origin-bottom-left={placement === 'top-start'}
      class:origin-top-right={placement === 'bottom-end'}
      class:origin-top-left={placement === 'bottom-start'}
      class:origin-bottom={placement === 'top'}
      class:origin-top={placement === 'bottom'}
      class:sm:text-body-2xs={size === 'sm'}
    >
      <section
        class="scrollbar-hide max-h-full w-full overflow-y-auto p-1 {desktopPadding
          ? 'pb-4'
          : ''} {scrollerClass} {gapClass}"
        style={desktopHeight}
      >
        <slot />
      </section>
      <!-- fading overlay for scroll hint -->
      <span class="{fadeClass} from-ondark-low bottom-2 h-4 {desktopPadding ? '' : 'opacity-0'}"></span>
    </div>
  {/if}
</div>

<style lang="postcss">
  /* for dropdown menu items, and dividers */
  .dropdown-small :global(.dropdown-menu-item) {
    @apply py-1.5;
  }
</style>
